import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ActivityTracker from './components/ActivityTracker';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { useAuthStore } from './store/authStore';

// Layout
import MainLayout from './components/Layout/MainLayout';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Impersonate from './pages/Impersonate';
import PublicBooking from './pages/PublicBooking';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Contacts from './pages/Contacts';
import Granola from './pages/Granola';
import MassMessaging from './pages/MassMessaging';
import Billing from './pages/Billing';
import SpruceInbox from './pages/SpruceInbox';
import Admin from './pages/Admin';
import Profile from './pages/Profile';
import PatientDetails from './pages/PatientDetails';
import AskLevitanAI from './pages/AskLevitanAI';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc',
      light: '#3384d7',
      dark: '#004999',
    },
    secondary: {
      main: '#7c3aed',
    },
    success: {
      main: '#10b981',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#dc2626',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated || !token) {
    if (location.pathname !== '/login') {
      sessionStorage.setItem('post_login_redirect', `${location.pathname}${location.search}`);
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};


function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <BrowserRouter>
            <ActivityTracker />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/impersonate" element={<Impersonate />} />
              {/* Patients open this from a texted link. No account, no session,
                  and no way into the rest of the app from here. */}
              {/* /b/ is what a link is minted as now: short enough to read
                  in a text. /book/ stays for the links already sent. */}
              <Route path="/b/:token" element={<PublicBooking />} />
              <Route path="/book/:token" element={<PublicBooking />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="granola" element={<Granola />} />
                <Route path="bulk-messaging" element={<MassMessaging />} />
                <Route path="billing" element={<Billing />} />
                <Route path="spruce-inbox" element={<SpruceInbox />} />
                <Route path="admin" element={<Admin />} />
                <Route path="ask-ai" element={<AskLevitanAI />} />
                <Route path="profile" element={<Profile />} />
                <Route path="patient-details/:spruceEntityId" element={<PatientDetails />} />
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
